export default {
    id: 'updateMonthsGraphPlugin',
    // Add padding to the chart layout to ensure buttons are not cut off
    beforeLayout: (chart) => {
        const pluginOptions = chart.options.plugins.updateMonthsGraphPlugin;

        if (!pluginOptions || !pluginOptions.enabled) return;

        const { buttonSize = 14 } = pluginOptions;

        // Base padding for next button
        const requiredPaddingRight = (buttonSize * 2) + 10;

        chart.options.layout = chart.options.layout || {};
        chart.options.layout.padding = chart.options.layout.padding || {};

        chart.options.layout.padding.right = Math.max(chart.options.layout.padding.right ?? 0, requiredPaddingRight);
    },
    // Draw the navigation buttons after the chart is drawn
    afterDraw: (chart) => {
        const pluginOptions = chart.options.plugins.updateMonthsGraphPlugin;

        if (!pluginOptions || !pluginOptions.enabled) return;

        const { ctx, scales: {'x-axis-0': xAxis}} = chart;
        const {
            buttonSize = 10,
            buttonColor = '#5E45D9',
            hoverColor = '#341F99',
            arrowColor = '#FFFFFF',
            showResetButton = false,
            canPreviousMonth = true,
            canNextMonth = true,
            buttonsDisabled = false,
            isLoading = false,
            onPrevClick = () => {},
            onNextClick = () => {},
            onResetClick = () => {},
        } = pluginOptions;

        if (!xAxis) return console.warn("L'axe 'x-axis-0' n'a pas été trouvé. Le plugin de navigation pourrait ne pas s'afficher correctement.");

        // Calculate the vertical position (centered on the X axis)
        const yPos = xAxis.top + (xAxis.height / 1.5);

        // Calculate the horizontal position of the buttons
        const prevButtonX = xAxis.left - buttonSize - 5;
        const nextButtonX = xAxis.right + 5;

        // Initialize buttons array
        const buttonsStateBefore = chart._chartjsNavigationButtons ?? [];
        chart._chartjsNavigationButtons = [];

        // Add Reset button if enabled
        if (showResetButton) {
            const resetButtonX = xAxis.left - (buttonSize * 2) - 20; // Position for the reset button
            const isResetButtonHover = buttonsStateBefore.some(button => button.id === 'reset' && button.hover);
            const resetButtonArea = {
                x: resetButtonX - buttonSize,
                y: yPos - buttonSize,
                width: buttonSize * 2,
                height: buttonSize * 2,
                hitRadius: buttonSize
            };
            chart._chartjsNavigationButtons.push({
                id: 'reset',
                x: resetButtonX,
                y: yPos,
                radius: buttonSize,
                area: resetButtonArea,
                hover: isResetButtonHover,
                disabled: buttonsDisabled,
                buttonColor,
                hoverColor,
                tooltipText: 'reset_date',
                onClick: onResetClick
            });
        }

        // Add Previous button
        const prevButtonArea = {
            x: prevButtonX - buttonSize,
            y: yPos - buttonSize,
            width: buttonSize * 2,
            height: buttonSize * 2,
            hitRadius: buttonSize
        };
        const isPrevButtonHover = buttonsStateBefore.some(button => button.id === 'prev' && button.hover);
        chart._chartjsNavigationButtons.push({
            id: 'prev',
            x: prevButtonX,
            y: yPos,
            radius: buttonSize,
            area: prevButtonArea,
            hover: isPrevButtonHover,
            disabled: !canPreviousMonth || buttonsDisabled,
            buttonColor,
            hoverColor,
            tooltipText: 'previous_month',
            onClick: onPrevClick
        });

        // Add Next button
        const nextButtonArea = {
            x: nextButtonX - buttonSize,
            y: yPos - buttonSize,
            width: buttonSize * 2,
            height: buttonSize * 2,
            hitRadius: buttonSize
        };
        const isNextButtonHover = buttonsStateBefore.some(button => button.id === 'next' && button.hover);
        chart._chartjsNavigationButtons.push({
            id: 'next',
            x: nextButtonX,
            y: yPos,
            radius: buttonSize,
            area: nextButtonArea,
            hover: isNextButtonHover,
            disabled: !canNextMonth || buttonsDisabled,
            buttonColor,
            hoverColor,
            tooltipText: 'next_month',
            onClick: onNextClick
        });


        // Drawing function for a button
        function drawButton(button) {
            ctx.save();
            ctx.beginPath();
            ctx.fillStyle = button.hover ? button.hoverColor : button.buttonColor;
            ctx.arc(button.x, button.y, button.radius, 0, Math.PI * 2, true);

            if (button.disabled) {
                ctx.fillStyle = '#ccc'; // Gray out the button if disabled
            }

            ctx.fill();
            ctx.closePath();

            ctx.font = `${button.radius * 1.3}px sans-serif`;
            ctx.fillStyle = arrowColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Draw the appropriate symbol
            if (button.id === 'prev') {
                ctx.fillText('<', button.x - button.radius * 0.1, button.y);
            } else if (button.id === 'next') {
                ctx.fillText('>', button.x + button.radius * 0.1, button.y);
            } else if (button.id === 'reset') {
                // Option: use a simple text character for reset if FontAwesome is not used
                ctx.fillText('\u21BB', button.x, button.y); // Unicode clockwise arrow
            }

            ctx.save();
            ctx.restore();
        }

        // Draw the buttons
        chart._chartjsNavigationButtons.forEach(button => drawButton(button));

        ctx.globalAlpha = isLoading ? 0.8 : 1;
    },

    afterEvent: (chart, event) => {
        const { x, y } = event;
        const buttons = chart._chartjsNavigationButtons;

        if (!buttons) return;

        let redraw = false;
        let isCurrentButtonHoveredIsDisabled = false;
        let hoveredButtonInfo = null;

        buttons.forEach(button => {
            const isHovering =
                x >= button.area.x &&
                x <= button.area.x + button.area.width &&
                y >= button.area.y &&
                y <= button.area.y + button.area.height;

            if (isHovering !== button.hover) {
                button.hover = isHovering;
                redraw = true;
            }

            if (isHovering) {
                if (button.disabled) {
                    isCurrentButtonHoveredIsDisabled = true;
                }

                hoveredButtonInfo = button;
            }
        });

        const pluginOptions = chart.options.plugins.updateMonthsGraphPlugin;
        if (typeof pluginOptions.onButtonHover === 'function') {
            pluginOptions.onButtonHover(hoveredButtonInfo);
        }

        const anyButtonHovered = buttons.some(button => button.hover);
        chart.canvas.style.cursor = anyButtonHovered
            ? (isCurrentButtonHoveredIsDisabled ? 'not-allowed' : 'pointer')
            : 'default';

        if (redraw) chart.draw();

        // Handle click
        if (event.type === 'click') {
            buttons.forEach(button => {
                const distance = Math.sqrt(Math.pow(x - button.x, 2) + Math.pow(y - button.y, 2));
                if (distance <= button.radius && typeof button.onClick === 'function') {
                    !button.disabled && button.onClick(chart);
                }
            });
        }
    }
}
